import type { MonsterRank, Rng, Tier, TopicStat } from "./types";

const MIN_ATTEMPTS_FOR_ACCURACY = 5;

export function accuracyOf(stat: TopicStat | undefined): number {
  if (!stat || stat.attempts < MIN_ATTEMPTS_FOR_ACCURACY) return 0.5;
  return stat.correct / stat.attempts;
}

/** Weak topics get up to ~3× the weight of mastered ones; recently asked topics are halved. */
export function topicWeight(stat: TopicStat | undefined, askedRecently: boolean): number {
  const accuracy = accuracyOf(stat);
  let w = 1 + 2 * (1 - accuracy);
  if (askedRecently) w *= 0.5;
  return w;
}

export interface PickTopicOptions {
  topics: readonly string[];
  stats: Readonly<Record<string, TopicStat | undefined>>;
  /** Most recent topic ids, newest last. */
  recent: readonly string[];
  disabled?: readonly string[];
  rng: Rng;
}

export function pickTopic(opts: PickTopicOptions): string {
  const disabled = new Set(opts.disabled ?? []);
  let candidates = opts.topics.filter((t) => !disabled.has(t));
  if (candidates.length === 0) candidates = [...opts.topics];
  if (candidates.length === 0) throw new Error("pickTopic: no topics available");

  const recentSet = new Set(opts.recent.slice(-2));
  const weights = candidates.map((t) => topicWeight(opts.stats[t], recentSet.has(t)));
  const total = weights.reduce((a, b) => a + b, 0);
  let roll = opts.rng.next() * total;
  for (let i = 0; i < candidates.length; i++) {
    roll -= weights[i] as number;
    if (roll <= 0) return candidates[i] as string;
  }
  return candidates[candidates.length - 1] as string;
}

export function pickTier(rank: MonsterRank, accuracy: number, rng: Rng): Tier {
  let tier: Tier;
  switch (rank) {
    case "common":
      tier = rng.chance(0.6) ? 1 : 2;
      break;
    case "elite":
      tier = 2;
      break;
    case "boss":
      tier = rng.chance(0.5) ? 2 : 3;
      break;
  }
  if (accuracy < 0.4 && tier > 1) tier = (tier - 1) as Tier;
  return tier;
}

export function updateStat(
  stat: TopicStat | undefined,
  correct: boolean,
  timeMs: number,
  now = new Date(),
): TopicStat {
  const attempts = (stat?.attempts ?? 0) + 1;
  const prevAvg = stat?.avgTimeMs ?? 0;
  return {
    attempts,
    correct: (stat?.correct ?? 0) + (correct ? 1 : 0),
    avgTimeMs: Math.round(prevAvg + (timeMs - prevAvg) / attempts),
    lastSeen: now.toISOString(),
  };
}

export function isMastered(stat: TopicStat | undefined): boolean {
  return !!stat && stat.attempts >= 30 && stat.correct / stat.attempts >= 0.9;
}

export function needsPractice(stat: TopicStat | undefined): boolean {
  return !!stat && stat.attempts >= 5 && stat.correct / stat.attempts < 0.6;
}
